import React from 'react';
import MapViewUtils from '../backbone/views/map/MapViewUtils';
import Personalizer from 'personalizer';
import {
    ColorBlueLight3,
    ColorRedDark2,
    ColorDarkuiSurface3,
    ColorDarkuiSurface4,
    ColorRedDark3,
    ColorBlueLight150,
    ColorBlueLight2,
    ColorBlueDark3,
    ColorRedLight2
} from 'ui-connect/styles/default-theme';

import { getCookie } from 'utils/CookieUtil';
import { trailsHash } from 'services/omt/subscription/subscriptionFields';

//MAP_PROVIDER_KEY used to store map provider such as google here or baidu
import i18next from 'i18next';
import Translate from '../components/Translate';
import {
    conditionsTrailDamageHash,
    trailDamamageHash,
    trailHash,
    trailsReviewSortHash
} from '../services/omt/subscription/subscriptionFields';
export const MAP_PROVIDER_KEY = 'map_provider_type';
export const DEFAULT_MAP_PROVIDER_VALUE = 'google';
export const DEFAULT_MAP_PROVIDER_VALUE_CHINA = 'baidu';
//MAP_SETTINGS_TRAILS_KEY is used for map layout (satellite/terrain) but also popularityHeatMap, lapMarkers and many more
export const MAP_SETTINGS_TRAILS_KEY = 'Map.Setting.Trails';
export const DEFAULT_MAP_SETTINGS_TRAILS_VALUE = `{"courseMarkers":false,"lapMarkers":false,"popularityHeatMap":"none", "popularityHeatMapCheckStatus": false, "${trailsHash}": false,"showTutorial":true, "hybrid":false,"sattelite":false,"terrain":true, "explorerTab":"Nearby", "${trailsHash}ListNearbySort":"highest-rated", "${trailsHash}ListSavedSort":"highest-rated", "${trailsReviewSortHash}":"most-recent"}`;

export const TRAILS_LIST_TABS = {
    NEARBY: {
        value: 'Nearby',
        tabIndex: 0
    },
    SAVED: {
        value: 'Saved',
        tabIndex: 1
    }
};

export const hasTrailsButWereFilteredOut = (totalNumberOfTrails, numberOfFilteredTrails) => {
    return totalNumberOfTrails > 0 && numberOfFilteredTrails === 0;
};

export const TRAIL_LIST_SORT_TYPES = () => [
    {
        value: 'HIGHEST_RATED_DESC',
        preferenceValue: 'highest-rated',
        label: i18next.t(`${trailsHash}:highest_rated_label`)
    },
    {
        value: 'MOST_POPULAR_DESC',
        preferenceValue: 'most-popular',
        label: i18next.t(`${trailsHash}:most_reviews_label`)
    }
];

export const sortTrailsList = (list, sortValue) => {
    let newList = [...list];

    if (sortValue && newList.length) {
        if (sortValue === TRAIL_LIST_SORT_TYPES()[0].preferenceValue) {
            return newList.sort((a, b) => (b.courseRating || 0) - (a.courseRating || 0));
        } else if (sortValue === TRAIL_LIST_SORT_TYPES()[1].preferenceValue) {
            return newList.sort((a, b) => (b.tripReportCount || 0) - (a.tripReportCount || 0));
        }
    }

    return newList;
};

export const sortMyReviewsList = (list, sortValue) => {
    let newList = [...list];

    if (sortValue && newList.length) {
        if (sortValue === MY_TRAIL_REVIEWS_SORT_TYPES()[0].preferenceValue) {
            return newList.sort((a, b) => b.activityDateMs - a.activityDateMs);
        } else if (sortValue === MY_TRAIL_REVIEWS_SORT_TYPES()[1].preferenceValue) {
            return newList.sort((a, b) => a.activityDateMs - b.activityDateMs);
        } else if (sortValue === MY_TRAIL_REVIEWS_SORT_TYPES()[2].preferenceValue) {
            return newList.sort((a, b) => b.helpfulRatingCount - a.helpfulRatingCount);
        } else if (sortValue === MY_TRAIL_REVIEWS_SORT_TYPES()[3].preferenceValue) {
            return newList.sort((a, b) => {
                const nameA = a.courseName.toUpperCase(); // ignore upper and lowercase
                const nameB = b.courseName.toUpperCase(); // ignore upper and lowercase
                if (nameA < nameB) {
                    return -1;
                }
                if (nameA > nameB) {
                    return 1;
                }

                // names must be equal
                return 0;
            });
        }
    }

    return newList;
};

export const convertWKTToCoordinates = (wkt) => {
    const processed = wkt
        .slice(11)
        .replace(/([()])/g, '')
        .split(', ')
        .map((i) => {
            let [lng, lat] = i.split(' ');
            return { lat: +lat, lon: +lng };
        });

    return processed;
};

export const difficultyMapping = () => ({
    1: { color: ColorBlueLight3, name: i18next.t('training_plans:activity_rating_easy') },
    2: { color: ColorRedDark2, name: i18next.t('training_plans:activity_rating_moderate') },
    3: { color: ColorDarkuiSurface3, name: i18next.t('training_plans:activity_rating_hard') }
});

export const POLYLINE_WEIGHT = 2;
export const HOVERED_POLYLINE_WEIGHT = 3;
export const POLYLINE_OUTLINE_WEIGHT = 4;
export const HOVERED_POLYLINE_OUTLINE_WEIGHT = 5;

/** This function takes a seconds param and returns hours ceiled to nearest half e.g. 1500 seconds -> 0.5 hours */
export const convertSecondsToHours = (seconds) => {
    return Math.ceil((seconds / 3600) * 2) / 2;
};

export const renderTrailDuration = (trail) => {
    const roundedMin = convertSecondsToHours(trail.durationMinSeconds);
    const roundedMax = convertSecondsToHours(trail.durationMaxSeconds);
    // If upper and lower round to the same value, set lower = upper minus 0.5
    if (roundedMin === roundedMax) {
        return (
            <>
                {convertSecondsToHours(trail.durationMinSeconds) - 0.5}-
                {convertSecondsToHours(trail.durationMaxSeconds)} <Translate ns="userprofile" content="hours" />{' '}
            </>
        );
    }
    // If lower value is less than 0.5, it will be floored to 0. UX items should read "< Upper" eg "< 1 hr"
    else if (trail.durationMinSeconds <= 1800) {
        return (
            <>
                {' '}
                &lt; {convertSecondsToHours(trail.durationMaxSeconds)}{' '}
                {trail.durationMaxSeconds <= 3600 ? (
                    <Translate ns="units" content="duration_hour" />
                ) : (
                    <Translate ns="userprofile" content="hours" />
                )}{' '}
            </>
        );
    }
    // If lower value is > 0.5, it will be ceiled to the nearest 0.5 (30 min).
    // Upper values will be ceiled to the nearest 0.5 (30 min).
    else {
        return (
            <>
                {convertSecondsToHours(trail.durationMinSeconds)}-{convertSecondsToHours(trail.durationMaxSeconds)}{' '}
                <Translate ns="userprofile" content="hours" />{' '}
            </>
        );
    }
};

/** get mapProvider base on props and china mode */
export const getMapProvider = () => {
    let mapProvider = DEFAULT_MAP_PROVIDER_VALUE;

    if (MapViewUtils.userInChina()) {
        mapProvider = DEFAULT_MAP_PROVIDER_VALUE_CHINA;
    } else if (mapProvider === DEFAULT_MAP_PROVIDER_VALUE_CHINA) {
        mapProvider = DEFAULT_MAP_PROVIDER_VALUE;
    }
    return mapProvider;
};

export const trailTypeMapping = () => ({
    POINT_TO_POINT: i18next.t(`${trailsHash}:point_to_point_label`),
    LOOP: i18next.t(`${trailsHash}:loop_label`),
    OUT_AND_BACK: i18next.t(`${trailsHash}:out_and_back_label`)
});

export const starRatingOptions = () => [
    { content: i18next.t(`${trailsHash}:any_label`), type: 'ANY' },
    { content: '1', type: '1_STAR' },
    { content: '2', type: '2_STAR' },
    { content: '3', type: '3_STAR' },
    { content: '4+', type: '4_STAR' }
];

export const difficultyOptions = () => [
    { content: i18next.t(`${trailsHash}:any_label`), type: 'ANY' },
    { content: i18next.t('training_plans:activity_rating_easy'), type: '1_EASY' },
    { content: i18next.t('training_plans:activity_rating_moderate'), type: '2_MODERATE' },
    { content: i18next.t('training_plans:activity_rating_hard'), type: '3_HARD' }
];

export const trailTypeOptions = () => [
    { content: i18next.t(`${trailsHash}:any_label`), type: 'ANY' },
    { content: i18next.t(`${trailsHash}:loop_label`), type: 'LOOP' },
    { content: i18next.t(`${trailsHash}:out_and_back_label`), type: 'OUT_AND_BACK' },
    { content: i18next.t(`${trailsHash}:point_to_point_label`), type: 'POINT_TO_POINT' }
];

export const trailFeaturesOptions = () => [
    { content: i18next.t(`${trailsHash}:any_label`), type: 'ANY' },
    { content: i18next.t(`${trailsHash}:features_dog_friendly_label`), type: 'DOG_FRIENDLY' },
    { content: i18next.t(`${trailsHash}:features_no_dogs_label`), type: 'NO_DOGS' },
    { content: i18next.t(`${trailsHash}:features_waterfalls_label`), type: 'WATERFALLS' },
    { content: i18next.t(`${trailsHash}:features_lakes_label`), type: 'LAKES' },
    { content: i18next.t(`${trailsHash}:features_rivers_label`), type: 'RIVERS' },
    { content: i18next.t(`${trailsHash}:features_oceans_label`), type: 'OCEANS' },
    { content: i18next.t(`${trailsHash}:features_mountaineering_label`), type: 'MOUNTAINEERING' },
    { content: i18next.t(`${trailsHash}:features_steep_sections_label`), type: 'STEEP_SECTIONS' },
    { content: i18next.t(`${trailsHash}:features_forest_label`), type: 'FOREST' },
    { content: i18next.t(`${trailsHash}:features_flowers_label`), type: 'FLOWERS' },
    { content: i18next.t(`${trailsHash}:features_kid_friendly_label`), type: 'KID_FRIENDLY' },
    { content: i18next.t(`${trailsHash}:features_permit_required_label`), type: 'PERMIT_REQUIRED' },
    { content: i18next.t(`${trailsHash}:features_water_stations_label`), type: 'WATER_STATIONS' },
    { content: i18next.t(`${trailsHash}:features_reaches_peaks_label`), type: 'REACHES_PEAKS' },
    { content: i18next.t(`${trailsHash}:features_rough_road`), type: 'ROUGH_ROADS' }
];

export const trailConditionsOptions = () => [
    { content: i18next.t(`${trailsHash}:conditions_temporary_closure`), type: 'TEMPORARY_CLOSURE' },
    { content: i18next.t(`${trailsHash}:${conditionsTrailDamageHash}`), type: trailDamamageHash },
    { content: i18next.t(`${trailsHash}:conditions_wet`), type: 'WET' },
    { content: i18next.t(`${trailsHash}:conditions_muddy`), type: 'MUDDY' },
    { content: i18next.t(`${trailsHash}:conditions_ice_snow`), type: 'ICE_AND_SNOW' },
    { content: i18next.t(`${trailsHash}:conditions_flooded_sections`), type: 'FLOODED_SECTIONS' },
    { content: i18next.t(`${trailsHash}:conditions_bugs`), type: 'BUGS' },
    { content: i18next.t(`${trailsHash}:conditions_dusty`), type: 'DUSTY' },
    { content: i18next.t(`${trailsHash}:conditions_sandy`), type: 'SANDY' },
    { content: i18next.t(`${trailsHash}:conditions_overgrown`), type: 'OVERGROWN' },
    { content: i18next.t(`${trailsHash}:conditions_crowded`), type: 'CROWDED' }
];

export const getDifficultyValue = (difficulty) => {
    switch (difficulty) {
        case '1_EASY':
            return 1;
        case '2_MODERATE':
            return 2;
        case '3_HARD':
            return 3;
        default:
            return '';
    }
};

export const getDifficultyLabel = (difficulty) => {
    switch (difficulty) {
        case 1:
            return '1_EASY';
        case 2:
            return '2_MODERATE';
        case 3:
            return '3_HARD';
        default:
            return '';
    }
};

export const filterLimits = {
    MIN_DISTANCE: 0,
    MAX_DISTANCE_MI: 100,
    MAX_DISTANCE_KM: 150,
    MIN_ASCENT: 0,
    MAX_ASCENT_FT: 10000,
    MAX_ASCENT_M: 3000
};

export const getMinDistance = () => {
    return filterLimits['MIN_DISTANCE'];
};

export const getMaxDistance = () => {
    const distanceUnit = Personalizer.getDistanceUnitKey();
    const maxDistance =
        distanceUnit === 'distance_unit_metric' ? filterLimits['MAX_DISTANCE_KM'] : filterLimits['MAX_DISTANCE_MI'];
    return maxDistance;
};

export const getMinAscent = () => {
    return filterLimits['MIN_ASCENT'];
};

export const getMaxAscent = () => {
    const elevationUnit = Personalizer.getMetersFtUnitKey();
    const maxAscent =
        elevationUnit === 'elevation_unit_metric' ? filterLimits['MAX_ASCENT_M'] : filterLimits['MAX_ASCENT_FT'];
    return maxAscent;
};

const haversineDistance = (lat1, lon1, lat2, lon2) => {
    const earthRadiusKm = 6371; // Approximate radius of the Earth in kilometers

    // Convert latitude and longitude from degrees to radians
    const radLat1 = (Math.PI / 180) * lat1;
    const radLon1 = (Math.PI / 180) * lon1;
    const radLat2 = (Math.PI / 180) * lat2;
    const radLon2 = (Math.PI / 180) * lon2;

    const dLat = radLat2 - radLat1;
    const dLon = radLon2 - radLon1;

    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(radLat1) * Math.cos(radLat2) * Math.sin(dLon / 2) * Math.sin(dLon / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return earthRadiusKm * c * 1000; // Convert to meters
};

export const isPointOnMapLine = (point, mapLine, thresholdDistanceMeters) => {
    for (let i = 0; i < mapLine.length - 1; i++) {
        const segment = [mapLine[i], mapLine[i + 1]];
        const distanceToStart = haversineDistance(point.lat, point.lng, segment[0].lat, segment[0].lon);
        const distanceToEnd = haversineDistance(point.lat, point.lng, segment[1].lat, segment[1].lon);

        // Calculate the distance to the segment (not just the endpoints)
        const segmentLength = haversineDistance(segment[0].lat, segment[0].lon, segment[1].lat, segment[1].lon);

        // Check if the point is within the threshold distance to the segment
        if (
            distanceToStart <= thresholdDistanceMeters ||
            distanceToEnd <= thresholdDistanceMeters ||
            distanceToStart + distanceToEnd <= segmentLength + thresholdDistanceMeters
        ) {
            return true;
        }
    }

    return false;
};

export const monthCategories = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];

export const convertPopularityPercentages = (trail) => {
    const trailUsage = trail?.trailUsage || {};
    Object.keys(trailUsage).forEach((key) => (trailUsage[key] *= 100));

    return trailUsage;
};

//========================== START OF FILTERS ==========================

export function genericFilter(list, filterObject, ...filters) {
    // Apply each filter to the items array
    return list.filter((item) => {
        // Check if the item passes all filters
        return filters.every((filter) => filter(filterObject, item));
    });
}

/**
 * For filtering numbers between min and max values, with a check for the maximum value to allow values bigger than that
 * @param {*} number - the number to check value against the filters
 * @param {*} min - the minimum value allowed for the value in the filter
 * @param {*} max - the minimum value allowed for the value in the filter
 * @param {*} type (optional) - 'distance' or 'ascent' to check for the max distance or max ascent
 * @returns true if the number passes the filter conditions, false otherwise
 */
function numberBetweenWithMax(number, min, max, type) {
    let maxValue;
    if (type === 'distance') {
        maxValue = getMaxDistance();
    } else if (type === 'ascent') {
        maxValue = getMaxAscent();
    }
    if (max === maxValue) {
        return number >= min;
    }
    return number >= min && number <= max;
}

/**
 * For filtering chip values with certain conditions based on the 'type' param
 * @param {*} item - the chip value that needs to be checked against the filters
 * @param {*} values - the list of the values allowed for the chip
 * @param {*} type (optional) - 'rating', 'difficulty' or 'features' to adjust conditions accordingly for filters
 * @param {*} itemRatingCount (optional) - if not rated trails are displayed, we need to also check for the rating count
 * @param {*} displayNotRated (optional) - boolean value to include (or not) the trails that have no ratings
 * @returns true if the chip value passes the filter conditions, false otherwise
 */
function valueOfTypes(item, typeValues, type, itemRatingCount, displayNotRated) {
    if (typeValues.includes('ANY') && displayNotRated === undefined) {
        return true;
    }

    if (type === 'rating') {
        // treat cases where 'ANY' is selected
        if (typeValues.includes('ANY')) {
            if (displayNotRated) {
                return true;
            } else {
                return item > 0 && item <= 5 && itemRatingCount > 0;
            }
        }
        // treat cases where other star rating values are selected
        const parsedValues = typeValues.map((value) => parseInt(value.split('_')[0])); // get star rating as numbers
        if (displayNotRated) {
            return parsedValues.some(
                (value) =>
                    item === 0 ||
                    itemRatingCount === 0 ||
                    (value <= item && (value === 4 ? item <= value + 1 : item < value + 1)) // treat 4+ rating case
            );
        }
        return parsedValues.some(
            (value) =>
                value <= item &&
                (value === 4 ? item <= value + 1 : item < value + 1) && // treat 4+ rating case
                itemRatingCount > 0
        );
    } else if (type === 'difficulty') {
        const parsedValues = typeValues.map((value) => parseInt(value.split('_')[0])); // get difficulty as numbers
        return parsedValues.includes(item);
    } else if (type === 'features') {
        // filter trails that have all features given
        // If the list of feature tags on the trail ('item') is falsy, the trail can only pass this filter if 'ANY' is selected.
        return item && typeValues.every((value) => item.includes(value));
    }

    return typeValues.includes(item);
}

// if a certain filter is not set, skip it by returning true
const distanceFilter = (filterObject, item) =>
    (filterObject.minDistance || filterObject.minDistance === 0) && filterObject.maxDistance
        ? numberBetweenWithMax(
              parseFloat(Personalizer.convertDistance(item.courseDistanceCM / 100)),
              filterObject.minDistance,
              filterObject.maxDistance,
              'distance'
          )
        : true;
const ascentFilter = (filterObject, item) =>
    (filterObject.minAscent || filterObject.minAscent === 0) && filterObject.maxAscent
        ? numberBetweenWithMax(
              parseFloat(Personalizer.convertMetersFt(item.courseElevationGainCM / 100)),
              filterObject.minAscent,
              filterObject.maxAscent,
              'ascent'
          )
        : true;
const ratingFilter = (filterObject, item) =>
    filterObject.starRating
        ? valueOfTypes(
              item.courseRating,
              filterObject.starRating,
              'rating',
              item.tripReportCount,
              filterObject.displayNotRated
          )
        : true;
const difficultyFilter = (filterObject, item) =>
    filterObject.difficulty ? valueOfTypes(item.courseDifficulty, filterObject.difficulty, 'difficulty') : true;
const trailTypeFilter = (filterObject, item) =>
    filterObject.trailType ? valueOfTypes(item.courseTypeDTO, filterObject.trailType) : true;
const trailFeaturesFilter = (filterObject, item) =>
    filterObject.trailFeatures ? valueOfTypes(item.tags, filterObject.trailFeatures, 'features') : true;

// all the filters available for trails
const filterOptions = [
    distanceFilter,
    ascentFilter,
    ratingFilter,
    difficultyFilter,
    trailTypeFilter,
    trailFeaturesFilter
];

export function filterTrailsList(list, filterObject) {
    const newList = [...list];
    return genericFilter(newList, filterObject, ...filterOptions);
}

//========================== END OF FILTERS ==========================

export const TRAIL_REVIEWS_SORT_TYPES = () => [
    {
        value: 'MOST_RECENT',
        preferenceValue: 'most-recent',
        label: i18next.t('common:most_recent')
    },
    {
        value: 'MOST_HELPFUL',
        preferenceValue: 'most-helpful',
        label: i18next.t(`${trailsHash}:most_helpful_label`)
    }
];

export const REVIEWS_SORT_ORDER_TYPES = [
    {
        preferenceValue: 'most-recent',
        orderValue: 'ACTIVITY_DATE'
    },
    {
        preferenceValue: 'most-helpful',
        orderValue: 'HELPFULNESS_SCORE'
    }
];

export const MY_TRAIL_REVIEWS_SORT_TYPES = () => [
    {
        value: 'NEWEST_TO_OLDEST',
        preferenceValue: 'newest-to-oldest',
        label: i18next.t(`${trailsHash}:${trailHash}_review_sort_new_old`)
    },
    {
        value: 'OLDEST_TO_NEWEST',
        preferenceValue: 'oldest-to-newest',
        label: i18next.t(`${trailsHash}:${trailHash}_review_sort_old_new`)
    },
    {
        value: 'MOST_HELPFUL',
        preferenceValue: 'most-helpful',
        label: i18next.t(`${trailsHash}:most_helpful_label`)
    },
    {
        value: 'ALPHABETICAL',
        preferenceValue: 'alphabetical',
        label: i18next.t(`${trailsHash}:trail_review_sort_alphabetical`)
    }
];

export const reviewIsNotOnlyStarRating = (review) =>
    // cast to boolean
    !!(
        review?.rating &&
        (review?.reviewComment ||
            review?.difficulty ||
            review?.courseConditions?.length > 0 ||
            review?.images?.length > 0)
    );

export const DEVICE_SUPPORT_LINK =
    'https://support.garmin.com/sas/gcweb/?productId=73207&contentId=fdNpMJupH10OXQsWAyeIuA';

export const ANONYMOUS_AVATAR_URL = `/images/${trailsHash}/avatar-${trailsHash}-generic.png`;
export const TRAIL_DEFAULT_THUMBNAIL_URL = `/images/${trailsHash}/${trailHash}-default-thumbnail.png`;

export const MAX_IMAGES = 10;

export const imageFileArrayToFormData = (imageFileArray) => {
    let images = [];
    imageFileArray.forEach((imageFile) => {
        let bodyFormData = new FormData();
        bodyFormData.append('image', imageFile);
        images.push(bodyFormData);
    });
    return images;
};

export const imageFileToFormData = (imageFile) => {
    let bodyFormData = new FormData();
    bodyFormData.append('image', imageFile);

    return bodyFormData;
};

export const areArraysInSameOrderBasedOnField = (array1, array2, field) => {
    const minLength = Math.min(array1.length, array2.length);
    for (let i = 0; i < minLength; i++) {
        if (array1[i][field] !== array2[i][field]) {
            return false;
        }
    }
    return true; // All corresponding elements are in the same order based on the specified field
};

export const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5 MB

// Function to resize images larger than maxSize given
export const resizeImages = (imageFiles, maxSize, callback) => {
    const resizedImages = [];

    // Function to handle each image file
    function processImage(file) {
        const reader = new FileReader();

        reader.onload = function(e) {
            const img = new Image();
            img.onload = function() {
                // Resize the image if it's larger than maxSize
                if (file.size > maxSize) {
                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d');

                    // Calculate initial scale factor
                    let scaleFactor = Math.sqrt(maxSize / file.size); // Use square root to reduce both width and height

                    function resizeAndCheck() {
                        const newWidth = img.width * scaleFactor;
                        const newHeight = img.height * scaleFactor;

                        // Resize the canvas
                        canvas.width = newWidth;
                        canvas.height = newHeight;

                        // Draw the image on the canvas with the new dimensions
                        ctx.drawImage(img, 0, 0, newWidth, newHeight);

                        // Convert the canvas content back to a Blob
                        canvas.toBlob((blob) => {
                            if (blob.size > maxSize) {
                                // If the blob is still too large, reduce the scale factor and try again
                                scaleFactor *= 0.95; // Reduce the scale factor by 5%
                                resizeAndCheck();
                            } else {
                                // If the blob is small enough, add it to the array
                                addResizedImage(blob, img, canvas, file);
                            }
                        }, file.type);
                    }

                    resizeAndCheck();
                } else {
                    // If the image is already smaller than maxSize, add it unchanged
                    resizedImages.push(file);
                    // Continue processing the next image if any
                    processNextImage();
                }
            };
            img.src = e.target.result;
        };

        // Read the image file as a data URL
        reader.readAsDataURL(file);
    }

    function addResizedImage(blobOrFile, img, canvas, file) {
        // Clean up the canvas
        URL.revokeObjectURL(img.src);
        canvas.remove();

        // Add the resized image blob to the array
        const resizedFile = new File([blobOrFile], file.name, {
            type: file.type,
            lastModified: Date.now()
        });
        resizedImages.push(resizedFile);

        // Continue processing the next image if any
        processNextImage();
    }

    // Function to process the next image in the array
    function processNextImage() {
        if (imageFiles.length > 0) {
            processImage(imageFiles.shift());
        } else {
            // Call the callback function with the array of resized images
            callback(resizedImages);
        }
    }

    // Start processing the first image
    processNextImage();
};

export const getCenterOfBoundingBox = function(arr) {
    return arr.reduce(
        function(x, y) {
            return [x[0] + y[0] / arr.length, x[1] + y[1] / arr.length];
        },
        [0, 0]
    );
};

export function getCookieValue(cookie) {
    return getCookie(cookie);
}

export const isPointInBoundingBox = (lat, lon, northLat, southLat, westLon, eastLon) => {
    const isLatInRange = lat >= southLat && lat <= northLat;
    const isLonInRange = lon >= westLon && lon <= eastLon;

    return isLatInRange && isLonInRange;
};
